import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../api/axios';

const TabletDashboard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Checkbox state: { itemId: boolean }
  const [checkedItems, setCheckedItems] = useState(() => {
    const saved = localStorage.getItem('tablet_checked_items');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    localStorage.setItem('tablet_checked_items', JSON.stringify(checkedItems));
  }, [checkedItems]);

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      // Note: This endpoint is in zoho_routes.py
      // Ensure backend exposes it under /api/tablet/items or we need to route it correctly.
      // In zoho_routes, we defined @zoho_bp.route("/api/tablet/items").
      // zoho_bp is likely registered with url_prefix='/' or '/api'?
      // In backend/app/__init__.py it usually registers blueprints.
      // Let's assume the route is /api/tablet/items if zoho_bp is root, or /api/zoho/api/tablet/items?
      // Wait, zoho_bp usually registered at root? 
      // Let's check __init__.py later. Assuming /api/tablet/items based on standard practice.
      // If zoho_bp is registered without prefix, then the route is /api/tablet/items.
      
      const response = await api.get('/tablet/items');
      setItems(response.data.items || []);
    } catch (err) {
      console.error("Failed to fetch tablet items", err);
      setError('Failed to load items. Please ensure you are logged into the Admin Panel / Zoho is connected.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
        Buyer's Tablet Dashboard
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Search items..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <Box>
             <Typography variant="body2" color="textSecondary">
                Total Items: {items.length} | Showing: {filteredItems.length}
            </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" m={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ maxHeight: '80vh' }}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  Purchased?
                </TableCell>
                <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Item Name</TableCell>
                <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  Current Stock (A)
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  Amount Required (B)
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  Purchase Price (C)
                </TableCell>
                <TableCell align="right" sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  List Price (D)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => {
                const isChecked = !!checkedItems[item.item_id];
                // Highlight if Amount Required > 0
                const needsBuying = item.amount_required > 0;
                
                return (
                  <TableRow 
                    key={item.item_id}
                    hover
                    sx={{ 
                      backgroundColor: isChecked ? '#e8f5e9' : 'inherit',
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isChecked}
                        onChange={() => handleCheck(item.item_id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <Typography variant="body1" fontWeight={500}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        SKU: {item.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {item.stock_on_hand}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end">
                        {needsBuying && (
                            <Chip 
                                label="BUY" 
                                size="small" 
                                color="error" 
                                sx={{ mr: 1, height: 20, fontSize: '0.7rem' }} 
                            />
                        )}
                        <Typography 
                            fontWeight={needsBuying ? 'bold' : 'normal'}
                            color={needsBuying ? 'error' : 'textPrimary'}
                        >
                            {item.amount_required}
                        </Typography>
                      </Box>
                      {/* Optional: Show reorder level tooltip or small text if needed */}
                    </TableCell>
                    <TableCell align="right">
                      ${item.purchase_price.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      ${item.list_price.toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredItems.length === 0 && (
                <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        No items found.
                    </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default TabletDashboard;
